/**
 * CartaoDoComunicado — um post da Central no Início do portal (PR 5).
 *
 * É o cartão da `PortalCentralFeedPage` de hoje, na paleta do portal: tipo
 * (Aviso/Evento/Mensagem/Informação), setor, data, corpo HTML sanitizado,
 * anexos e o bloco de leitura obrigatória.
 *
 * As classes `prose` vêm iguais às da Central de hoje, mas o plugin
 * `@tailwindcss/typography` NÃO está registrado no `tailwind.config.ts`
 * (está só no package.json): `prose` não faz nada neste projeto. Com o
 * preflight do Tailwind, um post com parágrafos e listas vira um bloco sem
 * espaçamento e sem marcadores. Por isso o corpo recebe estilos explícitos
 * (`[&_p]`, `[&_ul]`, `[&_a]`…) — o mesmo caminho que o `FeedPost` interno
 * já tomou. Se um dia o plugin entrar, os explícitos continuam ganhando.
 */
import DOMPurify from "dompurify";
import { AlertTriangle, CheckCircle2, Clock, Loader2, Sparkles } from "lucide-react";
import { useId, useMemo } from "react";

import { FeedAttachments } from "@/components/feed/FeedAttachments";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { PortalFeedItem } from "@/services/portalApi";

import { TIPO_DO_COMUNICADO } from "./constantes";
import { dataHoraCompleta } from "./tempoRelativo";

// M9 (revisão final) — era `Omit<PortalFeedItem, "attachments"> &
// { attachments: FeedAttachment[] }`: um adaptador pra quando
// `PortalFeedItem.attachments` ainda era `kind?` opcional. O PR 2 já trocou
// esse tipo na API antes da Task 3 ligar os dados — hoje `PortalFeedItem` já
// declara `attachments: FeedAttachment[]` (kind obrigatório), então o Omit +
// interseção não mudava mais nada. Alias mantido (não o tipo cru) só para
// não reescrever as importações em `PortalInicio.tsx`/testes.
export type ComunicadoDoInicio = PortalFeedItem;

export interface CartaoDoComunicadoProps {
  post: ComunicadoDoInicio;
  /** Post aberto a partir de uma notificação (`?post=`): realce + `post-destacado`. */
  destacado?: boolean;
  aoConfirmarLeitura: (postId: string) => void;
  /** ESTE post está sendo confirmado (a página compara `variables === post.id`). */
  confirmando?: boolean;
}

// A primeira linha é a string da Central de hoje, na íntegra; o resto é o que
// faz o HTML de verdade ser legível (ver o comentário do módulo).
const CLASSES_DO_CORPO = cn(
  "prose prose-sm max-w-none overflow-x-auto break-words text-sm leading-relaxed [&_table]:border-collapse [&_th]:border [&_td]:border [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md",
  "text-slate-700",
  // P2 (revisão de design) — a 1440 px o corpo tem 666 px a 14 px ≈ 95
  // caracteres por linha (confortável: 60–80). Só os parágrafos: imagem,
  // tabela e vídeo continuam na largura toda do cartão.
  "[&_p]:my-2 [&_p]:max-w-[75ch] [&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
  "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5",
  "[&_h1]:mt-4 [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:text-emerald-900",
  "[&_h2]:mt-4 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-emerald-900",
  "[&_h3]:mt-3 [&_h3]:font-semibold [&_h3]:text-emerald-900",
  "[&_a]:text-emerald-700 [&_a]:underline [&_a]:underline-offset-2",
  "[&_blockquote]:border-l-2 [&_blockquote]:border-emerald-200 [&_blockquote]:pl-3 [&_blockquote]:text-slate-600",
  "[&_th]:border-slate-200 [&_th]:bg-emerald-50/60 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border-slate-200 [&_td]:px-2 [&_td]:py-1",
  "[&_hr]:my-4 [&_hr]:border-emerald-100"
);

export function CartaoDoComunicado({
  post,
  destacado = false,
  aoConfirmarLeitura,
  confirmando = false,
}: CartaoDoComunicadoProps) {
  const idTitulo = useId();
  const idDestaque = useId();
  const obrigatoria = !!post.is_mandatory;
  const pendente = obrigatoria && !post.confirmed;
  const prazoVencido =
    pendente &&
    !!post.mandatory_deadline &&
    new Date(post.mandatory_deadline).getTime() < Date.now();

  // M13 (revisão final) — a lista de Comunicados e o contador de não lidas
  // fazem polling a cada 30s (usePortalComunicados/usePortalNotifications),
  // e QUALQUER poll re-renderiza todo cartão montado. Sem memoizar,
  // `DOMPurify.sanitize` rodava de novo em CADA post, a cada 30s, mesmo
  // quando `content_html` não mudou nada.
  const corpoSanitizado = useMemo(() => DOMPurify.sanitize(post.content_html), [post.content_html]);

  const cartao = (
    <article
      id={`comunicado-${post.id}`}
      aria-labelledby={idTitulo}
      // I4 (revisão final) — liga o rótulo "Em destaque" ao cartão pra quem
      // usa leitor de tela: antes ele era só um `<p>` IRMÃO do `<article>`,
      // sem relação nenhuma com ele (ver abaixo). `tabIndex={-1}` deixa o
      // artigo focável PROGRAMATICAMENTE (não entra no Tab normal) — é nele
      // que `PortalInicio` chama `.focus()` depois do `scrollIntoView`.
      aria-describedby={destacado ? idDestaque : undefined}
      tabIndex={destacado ? -1 : undefined}
      data-testid="comunicado-card"
      data-post-id={post.id}
      className={cn(
        "rounded-lg border border-emerald-100/80 bg-white/80 shadow-sm backdrop-blur-sm",
        // Pendência de leitura ganha a faixa âmbar à esquerda: dá para achar
        // rolando a lista, sem ler cada cartão.
        pendente && "border-l-4 border-l-amber-400",
        destacado && "shadow-md ring-2 ring-emerald-500/50 focus-visible:outline-none"
      )}
    >
      <header className="space-y-2 p-4 pb-3 sm:p-6 sm:pb-3">
        {destacado && (
          <p
            id={idDestaque}
            className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-700"
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Em destaque
          </p>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge
            variant="outline"
            className="border-emerald-200 bg-emerald-50 text-emerald-800"
            data-testid="comunicado-tipo"
          >
            {TIPO_DO_COMUNICADO[post.post_type] ?? post.post_type}
          </Badge>
          {obrigatoria && (
            <Badge
              variant="outline"
              className={cn(
                "gap-1",
                pendente
                  ? "border-amber-300 bg-amber-50 text-amber-900"
                  : "border-emerald-200 bg-white text-emerald-800"
              )}
            >
              {pendente ? (
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
              ) : (
                <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
              )}
              Leitura obrigatória
            </Badge>
          )}
        </div>
        <h3 id={idTitulo} className="text-lg font-semibold leading-snug text-slate-900">
          {post.title}
        </h3>
        <p className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
          {post.sector_name && (
            <>
              <span>{post.sector_name}</span>
              <span aria-hidden="true">•</span>
            </>
          )}
          <time dateTime={post.published_at}>{dataHoraCompleta(post.published_at)}</time>
        </p>
      </header>

      <div className="space-y-4 p-4 pt-0 sm:p-6 sm:pt-0">
        <div
          className={CLASSES_DO_CORPO}
          data-testid="comunicado-corpo"
          dangerouslySetInnerHTML={{ __html: corpoSanitizado }}
        />

        <FeedAttachments attachments={post.attachments} />

        {obrigatoria && (
          <div
            className="flex flex-wrap items-center gap-3 border-t border-emerald-50 pt-4"
            data-testid="comunicado-leitura-obrigatoria"
          >
            {post.confirmed ? (
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700">
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                Leitura confirmada
              </span>
            ) : (
              <>
                {post.mandatory_deadline && (
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 text-xs",
                      prazoVencido ? "font-medium text-red-700" : "text-slate-600"
                    )}
                  >
                    <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                    Prazo: {dataHoraCompleta(post.mandatory_deadline)}
                    {prazoVencido && " — vencido"}
                  </span>
                )}
                <Button
                  type="button"
                  onClick={() => aoConfirmarLeitura(post.id)}
                  disabled={confirmando}
                  data-testid={`confirm-read-${post.id}`}
                  className="h-12 bg-emerald-700 px-5 text-sm text-white hover:bg-emerald-800 sm:h-10"
                >
                  {confirmando ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      Confirmando…
                    </>
                  ) : (
                    "Confirmar leitura"
                  )}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </article>
  );

  if (!destacado) return cartao;

  // I4 (revisão final) — o rótulo "Em destaque" morou aqui, IRMÃO do
  // `<article>`: dava pra ver, mas um leitor de tela que chegasse direto no
  // artigo (ex.: navegação por landmark/heading) nunca o ouvia. Agora o
  // texto mora DENTRO do `<header>` do cartão (acima) e o wrapper só sobra
  // pelo testid `post-destacado` — um elemento não pode ter dois. Sem
  // classe (P6): com um filho só, o `space-y-2` que havia aqui não fazia nada.
  return <div data-testid="post-destacado">{cartao}</div>;
}

export default CartaoDoComunicado;
